/**
 * Facility & Provider Map — admin.
 *
 * Two views over the same geography:
 *
 *   Network   every geo-anchored facility, heat-weighted by patient volume
 *   Provider  one provider's home and the facilities they actually work,
 *             weighted by completed visits — the logistics picture
 *
 * The provider view answers the question the network view cannot: not "where
 * are our facilities" but "is this person's route sane".
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, MapPin, AlertTriangle, Route, Users } from 'lucide-react';

import { apiRequest } from '../../services/api';
import { Button } from '../../components/new-components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/new-components/ui/card';
import { Badge } from '../../components/new-components/ui/badge';
import FacilityHeatMap, { GeoFacility, GeoProvider } from '../../components/emerge/FacilityHeatMap';

type ApiResult<T> = { success: boolean; data: T; error?: string };

type NetworkPayload = {
  facilities: GeoFacility[];
  excluded_out_of_bounds: number;
  bounds: { minLat: number; maxLat: number; minLng: number; maxLng: number };
};

type ProviderRow = GeoProvider & {
  job_title?: string | null;
  monday_user_id?: number | null;
  geocoded: boolean;
};

type ProviderMap = {
  provider: ProviderRow;
  facilities: GeoFacility[];
  unlinked_reason?: string;
};

const fmt = new Intl.NumberFormat('en-US');

export default function EmergeFacilityMap() {
  const [network, setNetwork] = useState<NetworkPayload | null>(null);
  const [providers, setProviders] = useState<ProviderRow[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [providerMap, setProviderMap] = useState<ProviderMap | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const [net, provs] = await Promise.all([
          apiRequest('GET', '/emerge/geo/facilities') as Promise<ApiResult<NetworkPayload>>,
          apiRequest('GET', '/emerge/geo/providers') as Promise<ApiResult<ProviderRow[]>>,
        ]);
        setNetwork(net.data);
        setProviders(provs.data || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not load map data');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const selectProvider = useCallback(async (employeeId: string | null) => {
    setSelected(employeeId);
    setProviderMap(null);
    if (!employeeId) return;
    const res = (await apiRequest('GET', `/emerge/geo/providers/${employeeId}/map`)) as ApiResult<ProviderMap>;
    setProviderMap(res.data);
  }, []);

  const shown = providerMap ? providerMap.facilities : (network?.facilities ?? []);
  const shownProviders = useMemo<GeoProvider[]>(
    () => (providerMap?.provider ? [providerMap.provider] : []),
    [providerMap]
  );

  const totals = useMemo(() => ({
    facilities: shown.length,
    patients: shown.reduce((n, f) => n + (f.patient_count || 0), 0),
    aids: shown.reduce((n, f) => n + (f.hearing_aids_dispensed_count || 0), 0),
    visits: shown.reduce((n, f) => n + (f.completed_visit_count || 0), 0),
  }), [shown]);

  const ungeocoded = providers.filter((p) => !p.geocoded);

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 py-24 text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading map
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Facility &amp; Provider Map</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Geo-anchored facilities weighted by patient volume. Select a provider to see
          the facilities they work and how often.
        </p>
      </div>

      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {/* Data-quality notices. Surfaced rather than silently filtered — an
          excluded facility is a row someone needs to fix, not a non-event. */}
      {Boolean(network?.excluded_out_of_bounds) && (
        <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 px-4 py-3 text-sm">
          <AlertTriangle className="w-4 h-4 mt-0.5 text-amber-600 shrink-0" />
          <span>
            <strong>{network?.excluded_out_of_bounds}</strong>{' '}
            geo-anchored {network?.excluded_out_of_bounds === 1 ? 'facility has' : 'facilities have'}{' '}
            coordinates outside Florida and {network?.excluded_out_of_bounds === 1 ? 'is' : 'are'} hidden.
            Left on the map {network?.excluded_out_of_bounds === 1 ? 'it' : 'they'} would stretch the
            view across the Pacific.
          </span>
        </div>
      )}

      {ungeocoded.length > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 px-4 py-3 text-sm">
          <MapPin className="w-4 h-4 mt-0.5 text-amber-600 shrink-0" />
          <span>
            No home location for{' '}
            <strong>{ungeocoded.map((p) => `${p.first_name} ${p.last_name}`).join(', ')}</strong>
            {' '}— their address did not resolve. They appear in the list but not on the map.
          </span>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <Button
          variant={selected === null ? 'default' : 'outline'}
          size="sm"
          onClick={() => selectProvider(null)}
        >
          <Users className="w-4 h-4 mr-2" /> Whole network
        </Button>
        {providers.map((p) => (
          <Button
            key={p.employee_id}
            variant={selected === p.employee_id ? 'default' : 'outline'}
            size="sm"
            onClick={() => selectProvider(p.employee_id)}
          >
            {p.first_name} {p.last_name}
            {!p.geocoded && <span className="ml-1 opacity-60">·</span>}
          </Button>
        ))}
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        {([
          ['Facilities', totals.facilities],
          ['Patients', totals.patients],
          ['Aids dispensed', totals.aids],
          ...(providerMap ? [['Visits completed', totals.visits] as const] : []),
        ] as ReadonlyArray<readonly [string, number]>).map(([label, value]) => (
          <Card key={label}>
            <CardContent className="py-4">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
              <div className="text-2xl font-semibold" style={{ fontVariantNumeric: 'tabular-nums' }}>
                {fmt.format(value)}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {providerMap?.unlinked_reason && (
        <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 px-4 py-3 text-sm">
          <AlertTriangle className="w-4 h-4 mt-0.5 text-amber-600 shrink-0" />
          <span>{providerMap.unlinked_reason}</span>
        </div>
      )}

      <Card>
        <CardContent className="p-3">
          <FacilityHeatMap
            facilities={shown}
            providers={shownProviders}
            connectProviderHome={Boolean(providerMap)}
            showHeat={!providerMap}
            height={600}
          />
        </CardContent>
      </Card>

      {providerMap && providerMap.facilities.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Route className="w-4 h-4" />
              {providerMap.provider.first_name} {providerMap.provider.last_name} — facilities worked
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="py-2 pr-3 font-medium">Facility</th>
                    <th className="py-2 pr-3 font-medium">Region</th>
                    <th className="py-2 pr-3 font-medium text-right">Completed</th>
                    <th className="py-2 pr-3 font-medium text-right">Patients</th>
                    <th className="py-2 font-medium text-right">Aids</th>
                  </tr>
                </thead>
                <tbody>
                  {providerMap.facilities.map((f) => (
                    <tr key={f.facility_id} className="border-b last:border-0">
                      <td className="py-2 pr-3">{f.facility_name}</td>
                      <td className="py-2 pr-3 text-muted-foreground">
                        {f.region || '—'}
                        {f.facility_type_code && <Badge variant="secondary" className="ml-2">{f.facility_type_code}</Badge>}
                      </td>
                      <td className="py-2 pr-3 text-right" style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {fmt.format(f.completed_visit_count ?? 0)}
                      </td>
                      <td className="py-2 pr-3 text-right text-muted-foreground" style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {fmt.format(f.patient_count)}
                      </td>
                      <td className="py-2 text-right text-muted-foreground" style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {fmt.format(f.hearing_aids_dispensed_count)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
